@if (isLoading) {
<lib-loading-indicator [isLoading]="isLoading"></lib-loading-indicator>
}

<!-- The Latency Policies listing's own shape: a stacked column of bordered `.nf-panel`
     cards from libs/shared-assets/styles/nf-panels.scss. This page writes what that page
     lists, so the two are read one after the other and a different card treatment on each
     reads as two different products. -->
<div class="nf-policy-form-wrap" id="LatencyPolicyFormPage" (keyup.escape)="close()">
    <div class="nf-policy-form">
        <!-- The page's own title and back arrow. The app header is not drawn over this url —
             neither form url is a case in the shell's `setupSubtitle`, so the default branch
             leaves it off — and without this the page would carry no title at all. Drawn the
             way the Network Controller detail page draws its own: the arrow hangs in the
             page's left padding so the title stays flush with the column of panels. -->
        <div class="nf-policy-form__header">
            <div class="nf-policy-form__title-row">
                <div
                    class="nf-policy-form__back"
                    id="LatencyPolicyBack"
                    (click)="close()"
                    matTooltip="Back to Latency Policies"
                    matTooltipPosition="below"
                >
                    <div class="nf-policy-form__back-icon icon-back-arrow"></div>
                </div>
                <div class="nf-policy-form__title-text">
                    <span class="nf-policy-form__title">{{ title }}</span>
                    <!-- On an edit, the type it is for: the title alone would not say which
                         of the listing's rows the reader opened. -->
                    @if (isEditing) {
                    <span class="nf-policy-form__title nf-policy-form__title--name">{{
                        alertTypeLabel(alertType)
                    }}</span>
                    }
                </div>
            </div>
        </div>

        <div class="nf-panel">
            <div class="nf-panel__header">
                <div class="nf-panel__heading">
                    <h2 class="nf-panel__title">What it applies to</h2>
                    <p class="nf-panel__subtitle">
                        The alert type this policy decides the thresholds for, and the components it covers.
                    </p>
                </div>
            </div>
            <div class="nf-panel__body">
                <!-- First, because it is the policy's key: the id is derived from the
                     network, the alert type and the component, so these fields decide which
                     policy this is rather than describing one. On an existing policy they
                     are shown and not editable. -->
                <div class="stacked-fields">
                    <div class="field-row">
                        <div class="field-label">
                            Alert type @if (!isEditing) {
                            <span class="field-label-suffix">REQUIRED</span>
                            }
                        </div>
                        @if (isEditing) {
                        <span class="field-value" id="LatencyPolicyAlertTypeValue">
                            {{ alertTypeLabel(alertType) }}
                        </span>
                        <!-- The id under the name: it is what a filter and a mute are
                                 written against, so it is copied rather than read. -->
                        <p class="latency-policy-note latency-policy-id">{{ alertType }}</p>
                        } @else {
                        <!-- Cards, the same control the kind picker below uses and the
                                 same one the mute dialog asks "what to mute" with. This is
                                 the same kind of choice: a few options whose consequences
                                 need a line of prose each, not a label beside a dot.

                                 A closed list, not a free-text box. The service says which
                                 alert types a latency policy decides the thresholds for; a
                                 policy written against any other type is stored, served
                                 back, and never read by a detector — a setting that silently
                                 does nothing. The list comes from `/alert-types`, so a
                                 detector shipping a new latency rule appears here without
                                 this console changing.

                                 The detail line is what the type is keyed by, because that
                                 is what the reader needs before choosing: it decides how
                                 many id boxes appear below and what goes in them. -->
                        <div
                            class="latency-policy-options latency-policy-options--wrap"
                            id="LatencyPolicyAlertType"
                            role="radiogroup"
                            aria-label="Alert type"
                        >
                            @for (option of alertTypeOptions; track option.alertType) {
                            <button
                                type="button"
                                role="radio"
                                class="latency-policy-option"
                                id="LatencyPolicyAlertType{{ option.alertType }}"
                                [attr.aria-checked]="alertType === option.alertType"
                                [ngClass]="{ selected: alertType === option.alertType }"
                                (click)="setAlertType(option.alertType)"
                            >
                                <span class="latency-policy-option-mark" aria-hidden="true"></span>
                                <span class="latency-policy-option-text">
                                    <span class="latency-policy-option-title">
                                        {{ option.name || alertTypeLabel(option.alertType) }}
                                    </span>
                                    <span class="latency-policy-option-detail">
                                        {{ keyedBySentence(option.componentIdParts) }}
                                    </span>
                                    <!-- The id under the name: it is what a filter and a
                                             mute are written against, so it is copied rather
                                             than read. -->
                                    <span class="latency-policy-option-id">{{ option.alertType }}</span>
                                </span>
                            </button>
                            }
                        </div>
                        @if (alertTypeOptions.length === 0) {
                        <p class="latency-policy-note" id="LatencyPolicyNoAlertTypes">
                            No alert type here takes a latency policy, or the list could not be loaded. Reading alert
                            types needs its own permission, separate from reading alerts.
                        </p>
                        } }

                        <!-- What the chosen type actually watches for. The cards above
                                 carry what each type is keyed by, which is what the reader
                                 needs to choose between them; this is the one thing the
                                 registry cannot tell them, and it is too long to sit on a
                                 card.

                                 Only once a type is picked. Four of these on screen at once
                                 would be a wall of prose over the control that reads them. -->
                        @if (alertTypeWatches) {
                        <p class="latency-policy-type-note" id="LatencyPolicyTypeNote">{{ alertTypeWatches }}</p>
                        }

                        <!-- Also where the duplicate lands. A policy's id is derived from
                                 its network, alert type and component, so a key already
                                 covered is a fault on the alert type rather than a status the
                                 form has nowhere to show. -->
                        @if (errorFor('$.alertType')) {
                        <p class="latency-policy-field-error" id="LatencyPolicyAlertTypeError" role="alert">
                            {{ errorFor('$.alertType') }}
                        </p>
                        }
                    </div>

                    <!-- Shown on an existing policy only, where it is the key and
                             cannot be changed. There is no choice to make on a create: this
                             page writes component policies, and the network ones are edited
                             in the section over the listing. -->
                    @if (isEditing) {
                    <div class="field-row">
                        <div class="field-label">Scope</div>
                        <!-- One line per component: a link policy is keyed by two
                                 routers, and both have to be readable as separate ids. -->
                        <div class="latency-policy-scope-parts" id="LatencyPolicyScopeValue">
                            @for (part of lockedScopeParts; track $index) {
                            <span class="field-value">{{ part }}</span>
                            }
                        </div>
                        <p class="latency-policy-note" id="LatencyPolicyScopeLockedNote">
                            What a policy applies to cannot be changed. To point one at something else, delete this
                            policy and create another.
                        </p>
                    </div>
                    }

                    <!-- One box per id the alert type is keyed by, and the alert type
                             is what says how many: a link policy is keyed by the two edge
                             routers the link joins, a service policy by one service id.
                             Nothing here decides that — a detector can ship a rule keyed
                             differently, and the wrong count is a 400.

                             A fabric link id is deliberately not one of these. It names one
                             *instance* of a link and is replaced whenever the link re-forms,
                             so a policy written against one stops applying the moment the
                             link flaps. The pair of routers is the stable identity. -->
                    @if (!isEditing) { @if (componentIdParts.length === 0 && alertType) {
                    <div class="field-row">
                        <p class="latency-policy-note" id="LatencyPolicyNoParts">
                            This alert type is not about a component, so you cannot write a policy for one here.
                        </p>
                    </div>
                    }
                    <!-- Two columns, which is one rule for both shapes the service asks
                             for. A link's two router boxes sit side by side and read as the
                             pair they are: they are one key, and stacked they read as two
                             separate questions. A service policy's single box stops at half
                             the width rather than running the length of the form for one id.
                             A type keyed by three parts wraps, two then one. -->
                    <div class="latency-policy-scope-fields">
                        @for (label of componentIdLabels; track $index) {
                        <div class="latency-policy-scope-field">
                            <div
                                class="field-label"
                                [class.latency-policy-label-invalid]="hasFieldError(componentPath($index))"
                            >
                                {{ label }}
                                <span class="field-label-suffix">REQUIRED</span>
                            </div>
                            <!-- One control for both cases, because it is one field. A
                                     network's routers and services are searched at the
                                     controller as the reader types — `completeMethod` asks and
                                     `minLength` and `delay` decide when, so a network too big
                                     for one page still offers the component being looked for.

                                     `forceSelection` is what keeps the two cases honest. With a
                                     list behind it the box takes only what is in that list.
                                     Without one — a link, a detector, a listing this reader may
                                     not read — the same box takes the id as text, so a threshold
                                     can still be set.

                                     The control binds the option and the form keeps the id: the
                                     name is what the reader knows the component as, and the
                                     Ziti id is what a policy is keyed by.

                                     `appendTo="body"` because the panel would otherwise be cut
                                     off by the form container it is drawn inside — that wrapper
                                     is `overflow: hidden`. The panel is outside this component
                                     once it is there, which is why the colours it does not need
                                     are set on the control itself, and why its stacking has to
                                     be asked for: see {@link pickerOverlay}.

                                     The colours ride on `class` rather than on `styleClass`:
                                     both land on the host, and `styleClass` is deprecated from
                                     PrimeNG 20.

                                     **`[suggestions]` binds the entry itself, with no `|| []`
                                     fallback.** An `|| []` here builds a new array on every
                                     change detection cycle, and PrimeNG reads the first
                                     `suggestions` reference to change after it asks as the
                                     answer to that question: it opens the panel on it and stops
                                     listening. A reference that changed for any other reason
                                     spends the answer, and the results land to silence. The
                                     component keeps every entry sized and holds each one's
                                     identity for that reason — see `loadComponentOptions`. -->
                            <p-autoComplete
                                class="nf-autocomplete"
                                [class.nf-autocomplete--invalid]="hasFieldError(componentPath($index))"
                                [inputStyleClass]="'form-field-input'"
                                [inputId]="'LatencyPolicyComponent' + $index"
                                [ngModel]="componentPicks[$index]"
                                (ngModelChange)="takeComponentPick($index, $event)"
                                [suggestions]="componentSuggestions[$index]"
                                (completeMethod)="searchComponents($index, $event.query)"
                                optionLabel="name"
                                [forceSelection]="canPick($index)"
                                [dropdown]="canPick($index)"
                                [minLength]="searchMinLength"
                                [delay]="searchDelay"
                                [showClear]="true"
                                [placeholder]="componentPlaceholder($index, label)"
                                [emptyMessage]="canPick($index) ? 'No component of this kind matches' : ''"
                                [ariaLabel]="label"
                                appendTo="body"
                                [overlayOptions]="pickerOverlay"
                                (onBlur)="fieldBlurred(componentPath($index))"
                            ></p-autoComplete>
                            @if (errorFor(componentPath($index))) {
                            <p
                                class="latency-policy-field-error"
                                id="LatencyPolicyComponentError{{ $index }}"
                                role="alert"
                            >
                                {{ errorFor(componentPath($index)) }}
                            </p>
                            }
                        </div>
                        }
                    </div>
                    <!-- A fault the two boxes break together: the same edge router chosen
                         twice, which is wrong about the pair and about neither box on its
                         own. Under the boxes rather than beside one of them, because
                         attaching it to the second would say the second is the wrong one -
                         and either box is the one to change. The kind picker carries its xor
                         rule the same way. -->
                    @if (errorFor('$.componentIds')) {
                    <p class="latency-policy-field-error" id="LatencyPolicyComponentPairError" role="alert">
                        {{ errorFor('$.componentIds') }}
                    </p>
                    }
                    <!-- What this form itself refuses about the scope, and only where the
                             service named nothing. A component is required here: this page
                             writes an override for one component, and the thresholds for a
                             whole network are set in the Network Policy section. The message
                             under a box is the service's wherever there is one — this is what
                             the page says instead of nothing when Create is refused by its own
                             rule. -->
                    @if (scopeErrors.length > 0) {
                    <ul class="latency-policy-ladder-errors" id="LatencyPolicyScopeErrors" role="alert">
                        @for (error of scopeErrors; track error) {
                        <li>{{ error }}</li>
                        }
                    </ul>
                    } @if (componentIdParts.length > 0) {
                    <p class="latency-policy-note" id="LatencyPolicyScopeNote">
                        <!-- Only where there is an order to get wrong. One box has none,
                                 and a line about ordering beside a single field reads as a
                                 hint that something else is still to be filled in. -->
                        @if (componentIdParts.length > 1) { The order does not matter. } To set thresholds for the whole
                        network instead, use the Network Policy section on the Latency Policies page.
                    </p>
                    } }
                </div>
            </div>
        </div>

        <!-- Second, because the numbers only mean anything once the reader knows what they
             apply to — and the kind has to be picked before the numbers, because it decides
             what they are measured in. -->
        <div class="nf-panel">
            <div class="nf-panel__header">
                <div class="nf-panel__heading">
                    <h2 class="nf-panel__title">Thresholds</h2>
                    <p class="nf-panel__subtitle">When an alert of this type fires for the components above.</p>
                </div>
            </div>
            <div class="nf-panel__body">
                <!-- Why the boxes below are shut. A panel of dead controls with nothing said
                     about them asks the reader to work out what is missing, and the two
                     reasons are different questions: what the numbers are measured in, and
                     what they apply to. The second sentence is the one Create is refused
                     with, so the shut panel and the refusal cannot word it two ways. -->
                @if (thresholdsLockedReason) {
                <p class="latency-policy-note latency-policy-note--locked" id="LatencyPolicyThresholdsLocked">
                    {{ thresholdsLockedReason }}
                </p>
                }
                <div class="stacked-fields">
                    <div class="field-row">
                        <div class="field-label">How it measures</div>
                        <!-- The mute dialog's "What to mute" control, in the same
                                 shape: a stack of cards, each carrying a title and the
                                 sentence that says what picking it costs. The choice here
                                 is the same kind of choice - one of a few options whose
                                 consequences need a line of prose each, not a label beside
                                 a dot - so it looks the same. -->
                        <div
                            class="latency-policy-options"
                            [class.latency-policy-options--single]="isRatioOnly"
                            id="LatencyPolicyKind"
                            role="radiogroup"
                            aria-label="How it measures"
                        >
                            @for (entry of policyKinds; track entry.value) {
                            <button
                                type="button"
                                role="radio"
                                class="latency-policy-option"
                                id="LatencyPolicyKind{{ entry.value }}"
                                [attr.aria-checked]="kind === entry.value"
                                [ngClass]="{ selected: kind === entry.value }"
                                (click)="setKind(entry.value)"
                                [disabled]="!canEditThresholds"
                            >
                                <span class="latency-policy-option-mark" aria-hidden="true"></span>
                                <span class="latency-policy-option-text">
                                    <span class="latency-policy-option-title">{{ entry.label }}</span>
                                    <span class="latency-policy-option-detail">{{ entry.hint }}</span>
                                </span>
                            </button>
                            }
                        </div>
                        <!-- Why there is one card here rather than two. The missing card
                                 is not drawn disabled: a control nobody can use still asks
                                 the reader to work out why, and this answers it in a line. -->
                        @if (isRatioOnly) {
                        <p class="latency-policy-note" id="LatencyPolicyRatioOnly">{{ ratioOnlyNote }}</p>
                        }
                        <!-- The xor rule: a policy grades one way or the other, never
                                 both and never neither. It is a fault about the pair, so it
                                 sits under the picker rather than under a number. Either path
                                 can carry it; the first one the service named is shown. -->
                        @if (errorFor('$.auto') || errorFor('$.fixed')) {
                        <p class="latency-policy-field-error" id="LatencyPolicyKindError" role="alert">
                            {{ errorFor('$.auto') || errorFor('$.fixed') }}
                        </p>
                        }
                    </div>

                    @if (isAuto) {
                    <!-- Floor, the three multipliers, then the ceiling — on one line,
                             because that is the order a measurement passes through them and
                             reading it across is how somebody checks the ladder ascends.

                             The three in the middle are one scale and the two on the ends
                             are not, so the middle three carry a spanning label and a tint,
                             the same way the Network Policy table groups them. Without it
                             five boxes in a row read as five unrelated numbers. -->
                    <div class="field-row">
                        <div class="latency-policy-ladder" role="group" aria-label="Auto thresholds">
                            <div class="latency-policy-ladder__spacer" aria-hidden="true"></div>
                            <div class="latency-policy-ladder__group-label">
                                <span class="latency-policy-group-label__text">Multipliers</span>
                            </div>
                            <div class="latency-policy-ladder__spacer" aria-hidden="true"></div>

                            <div class="latency-policy-field">
                                <div
                                    class="field-label"
                                    [class.latency-policy-label-invalid]="hasFieldError('$.auto.floorMillis')"
                                >
                                    Floor
                                    <span class="field-label-suffix">REQ</span>
                                </div>
                                <div class="latency-policy-unit-input">
                                    <input
                                        class="form-field-input"
                                        id="LatencyPolicyFloor"
                                        [class.latency-policy-input-invalid]="hasFieldError('$.auto.floorMillis')"
                                        type="number"
                                        min="0"
                                        step="1"
                                        [placeholder]="placeholders.floorMillis"
                                        [(ngModel)]="floorMillis"
                                        (blur)="fieldBlurred('$.auto.floorMillis')"
                                        [disabled]="!canEditThresholds"
                                    />
                                    <span class="latency-policy-unit" aria-hidden="true">ms</span>
                                </div>
                            </div>

                            <div class="latency-policy-field latency-policy-field--grouped">
                                <div
                                    class="field-label"
                                    [class.latency-policy-label-invalid]="hasFieldError('$.auto.minorMultiplier')"
                                >
                                    Minor
                                    <span class="field-label-suffix">REQ</span>
                                </div>
                                <div class="latency-policy-unit-input">
                                    <input
                                        class="form-field-input"
                                        id="LatencyPolicyMinor"
                                        [class.latency-policy-input-invalid]="hasFieldError('$.auto.minorMultiplier')"
                                        type="number"
                                        min="1"
                                        step="any"
                                        [placeholder]="placeholders.minorMultiplier"
                                        [(ngModel)]="minorMultiplier"
                                        (blur)="fieldBlurred('$.auto.minorMultiplier')"
                                        [disabled]="!canEditThresholds"
                                    />
                                    <span class="latency-policy-unit" aria-hidden="true">×</span>
                                </div>
                            </div>

                            <div class="latency-policy-field latency-policy-field--grouped">
                                <div
                                    class="field-label"
                                    [class.latency-policy-label-invalid]="hasFieldError('$.auto.majorMultiplier')"
                                >
                                    Major
                                    <span class="field-label-suffix">REQ</span>
                                </div>
                                <div class="latency-policy-unit-input">
                                    <input
                                        class="form-field-input"
                                        id="LatencyPolicyMajor"
                                        [class.latency-policy-input-invalid]="hasFieldError('$.auto.majorMultiplier')"
                                        type="number"
                                        min="1"
                                        step="any"
                                        [placeholder]="placeholders.majorMultiplier"
                                        [(ngModel)]="majorMultiplier"
                                        (blur)="fieldBlurred('$.auto.majorMultiplier')"
                                        [disabled]="!canEditThresholds"
                                    />
                                    <span class="latency-policy-unit" aria-hidden="true">×</span>
                                </div>
                            </div>

                            <div class="latency-policy-field latency-policy-field--grouped">
                                <div
                                    class="field-label"
                                    [class.latency-policy-label-invalid]="hasFieldError('$.auto.criticalMultiplier')"
                                >
                                    Critical
                                    <span class="field-label-suffix">REQ</span>
                                </div>
                                <div class="latency-policy-unit-input">
                                    <input
                                        class="form-field-input"
                                        id="LatencyPolicyCritical"
                                        [class.latency-policy-input-invalid]="
                                            hasFieldError('$.auto.criticalMultiplier')
                                        "
                                        type="number"
                                        min="1"
                                        step="any"
                                        [placeholder]="placeholders.criticalMultiplier"
                                        [(ngModel)]="criticalMultiplier"
                                        (blur)="fieldBlurred('$.auto.criticalMultiplier')"
                                        [disabled]="!canEditThresholds"
                                    />
                                    <span class="latency-policy-unit" aria-hidden="true">×</span>
                                </div>
                            </div>

                            <!-- Absolute milliseconds, so it is absent on a type that
                                     grades a ratio and nothing else. The typed value is kept
                                     rather than cleared - the thresholds getter is what stops
                                     it reaching the wire. -->
                            @if (!isRatioOnly) {
                            <div class="latency-policy-field">
                                <div
                                    class="field-label"
                                    [class.latency-policy-label-invalid]="hasFieldError('$.auto.ceilingMillis')"
                                >
                                    Ceiling
                                    <span class="field-label-suffix">OPT</span>
                                </div>
                                <div class="latency-policy-unit-input">
                                    <input
                                        class="form-field-input"
                                        id="LatencyPolicyCeiling"
                                        [class.latency-policy-input-invalid]="hasFieldError('$.auto.ceilingMillis')"
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="none"
                                        [(ngModel)]="ceilingMillis"
                                        (blur)="fieldBlurred('$.auto.ceilingMillis')"
                                        [disabled]="!canEditThresholds"
                                    />
                                    <span class="latency-policy-unit" aria-hidden="true">ms</span>
                                </div>
                            </div>
                            }
                        </div>

                        <!-- What the service refused on this line, under the whole line rather than
                                 under the box it is about: a number column is too narrow to hold a sentence,
                                 and one wrapped under a box would push its neighbours out of line. Each names
                                 its column, and the outline and the red label point at which box. -->
                        @if (ladderErrors.length > 0) {
                        <ul class="latency-policy-ladder-errors" id="LatencyPolicyLadderErrors" role="alert">
                            @for (error of ladderErrors; track error) {
                            <li>{{ error }}</li>
                            }
                        </ul>
                        }

                        <!-- Under the row rather than under each box: the columns are
                                 too narrow to hold a sentence, and what each one does needs
                                 one. Three lines, because they are three different rules. -->
                        <ul class="latency-policy-rules">
                            <li>
                                <strong>Floor</strong> is a latency in milliseconds. Nothing faster than it alerts,
                                whatever the multiple — so a 3 ms link that reached 12 ms is a clean 4× and still
                                doesn't notify.
                            </li>
                            <li>
                                <strong>Multipliers</strong> are multiples of {{ multiplierBasis }}, not latencies: 3×
                                means three times what this component normally measures. They must go up in order, and
                                each must be above 1 — the normal level is itself a recent measurement.
                            </li>
                            @if (!isRatioOnly) {
                            <li>
                                <strong>Ceiling</strong> is always critical, whatever the normal level says. It is the
                                only thing that catches a component that has always been slow, and the only one that
                                works on a component with no normal level yet. Leave it empty to use multiples only.
                            </li>
                            }
                        </ul>
                    </div>
                    } @else {
                    <!-- The three fixed rungs on one line, grouped the same way the
                             multipliers are. No floor and no ceiling here: the critical
                             rung already is one. -->
                    <div class="field-row">
                        <div
                            class="latency-policy-ladder latency-policy-ladder--fixed"
                            role="group"
                            aria-label="Latencies"
                        >
                            <div class="latency-policy-ladder__group-label">
                                <span class="latency-policy-group-label__text">Latencies</span>
                            </div>

                            <div class="latency-policy-field latency-policy-field--grouped">
                                <div
                                    class="field-label"
                                    [class.latency-policy-label-invalid]="hasFieldError('$.fixed.minorMillis')"
                                >
                                    Minor
                                </div>
                                <div class="latency-policy-unit-input">
                                    <input
                                        class="form-field-input"
                                        id="LatencyPolicyMinorMillis"
                                        [class.latency-policy-input-invalid]="hasFieldError('$.fixed.minorMillis')"
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="100"
                                        [(ngModel)]="minorMillis"
                                        (blur)="fieldBlurred('$.fixed.minorMillis')"
                                        [disabled]="!canEditThresholds"
                                    />
                                    <span class="latency-policy-unit" aria-hidden="true">ms</span>
                                </div>
                            </div>

                            <div class="latency-policy-field latency-policy-field--grouped">
                                <div
                                    class="field-label"
                                    [class.latency-policy-label-invalid]="hasFieldError('$.fixed.majorMillis')"
                                >
                                    Major
                                </div>
                                <div class="latency-policy-unit-input">
                                    <input
                                        class="form-field-input"
                                        id="LatencyPolicyMajorMillis"
                                        [class.latency-policy-input-invalid]="hasFieldError('$.fixed.majorMillis')"
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="250"
                                        [(ngModel)]="majorMillis"
                                        (blur)="fieldBlurred('$.fixed.majorMillis')"
                                        [disabled]="!canEditThresholds"
                                    />
                                    <span class="latency-policy-unit" aria-hidden="true">ms</span>
                                </div>
                            </div>

                            <div class="latency-policy-field latency-policy-field--grouped">
                                <div
                                    class="field-label"
                                    [class.latency-policy-label-invalid]="hasFieldError('$.fixed.criticalMillis')"
                                >
                                    Critical
                                </div>
                                <div class="latency-policy-unit-input">
                                    <input
                                        class="form-field-input"
                                        id="LatencyPolicyCriticalMillis"
                                        [class.latency-policy-input-invalid]="hasFieldError('$.fixed.criticalMillis')"
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="1000"
                                        [(ngModel)]="criticalMillis"
                                        (blur)="fieldBlurred('$.fixed.criticalMillis')"
                                        [disabled]="!canEditThresholds"
                                    />
                                    <span class="latency-policy-unit" aria-hidden="true">ms</span>
                                </div>
                            </div>
                        </div>

                        <!-- Under the whole line, for the reason the auto ladder gives. -->
                        @if (ladderErrors.length > 0) {
                        <ul class="latency-policy-ladder-errors" id="LatencyPolicyFixedLadderErrors" role="alert">
                            @for (error of ladderErrors; track error) {
                            <li>{{ error }}</li>
                            }
                        </ul>
                        }

                        <ul class="latency-policy-rules">
                            <li>
                                A set number of milliseconds, the same for every component, measured from the first
                                reading with nothing to learn first.
                            </li>
                            <li>
                                Set at least one, and the ones you set must go up in order. A step left empty is never
                                reached rather than zero — a policy with only a critical step opens critical alerts and
                                nothing else.
                            </li>
                        </ul>
                    </div>
                    }
                </div>

                <!-- The one thing this page must not be read as offering. Somebody who set a
                     multiplier of 500 to silence something would find it still notifying, so
                     the alternative is named rather than left to be found.

                     At info, not warning: nothing is wrong, and nothing the reader does here
                     can make it so. Under the thresholds it qualifies and over the row that
                     saves them, which is where the mistake it heads off would be made. -->
                <div class="nf-callout nf-callout--info" id="LatencyPolicyMuteNote" role="note">
                    <span class="nf-callout__icon" aria-hidden="true">
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4M12 8h.01" />
                        </svg>
                    </span>
                    <span>
                        Thresholds decide when an alert fires, never whether anybody is told. To stop the notifications
                        without changing what is watched for, mute it instead.
                    </span>
                </div>
            </div>

            <!-- Every refusal is shown at the box it is about: under the id boxes, and
                 under the threshold line for the columns too narrow to hold a sentence.
                 There is no summary of them here. One would repeat what the boxes already
                 say, a page-length away from the fields the reader has to change, and the
                 two wordings would drift apart by the second release. `errors` stays the
                 gate on the save button — see `validate`.

                 A save the service refused outright is the exception: it belongs beside the
                 button that asked, so it sits directly over the action row. -->
            @if (saveError) {
            <div class="latency-policy-errors" id="LatencyPolicySaveError" role="alert">
                <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                <div>{{ saveError }}</div>
            </div>
            }

            <!-- The listing's own action row, at the foot of the last panel: the same
                 `.nf-panel__actions` and the same ghost/primary pair the Network Policy
                 section saves with. -->
            <div class="nf-panel__actions">
                <button type="button" class="nf-btn nf-btn--ghost" id="LatencyPolicyCancel" (click)="close()">
                    Cancel
                </button>
                <button
                    type="button"
                    class="nf-btn nf-btn--primary"
                    id="LatencyPolicySave"
                    [disabled]="processing"
                    (click)="save()"
                >
                    {{ processing ? 'Saving…' : isEditing ? 'Save' : 'Create' }}
                </button>
            </div>
        </div>
    </div>
</div>
